import { createSlice } from '@reduxjs/toolkit'
import { hasAlarmExpired, createAlarmTimeSecondsFromNow, getNextTimeOfDay } from '../../alarm/index.js'
import { storyPaths } from '../storyPaths.js'

// Every story path fires its own `endScene` action when the player finishes a
// scene in the Reader. Used to detect "the player just finished reading
// something" regardless of which path it was, for the free-play time-slot lock.
const endSceneTypes = new Set(storyPaths.map(({ slice }) => slice.actions.endScene.type))

const initialState = {
  isNoDelay: false, // False is "timescale mode", true is "free play"
  isNoNotifications: true, // true means they don't want notifications, false means they do
  hasStartedGame: false,
  gcdTimeSeconds: 20,
  // Debug-only: when set to 'morning'/'afternoon'/'evening'/'night', markers are
  // gated as if it were that time of day instead of the real wall-clock time.
  // null = use the real time. Lets the debug panel exercise time-of-day marker
  // visibility.
  debugTimeOfDay: null,
  // Free play has no wall clock, so it tracks its own time-of-day slot,
  // advanced manually by the player. Starts on 'evening' so the very first
  // slot lines up with Scientific Detective's opening scene.
  freePlayTimeOfDay: 'evening',
  // Once the player finishes any Reader scene in free play, the current time
  // slot is "used up" globally (across every path) - no more markers/
  // conversations for this slot until the player advances to the next one.
  freePlayTimeSlotConsumed: false,
  // Until the player advances time-of-day for the first time, free play is
  // restricted to Scientific Detective only (no other path's markers or
  // conversations show), so the opening slot is a single-storyline intro.
  freePlayHasAdvancedOnce: false
}

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setTimescaleMode: (state) => {
      state.isNoDelay = false
    },
    setNoDelayMode: (state) => {
      state.isNoDelay = true
    },
    setNoNotification: (state) => {
      state.isNoNotifications = true
    },
    setUseNotifications: (state) => {
      state.isNoNotifications = false
    },
    setGameHasStarted: (state) => {
      state.hasStartedGame = true
    },
    setDebugTimeOfDay: (state, action) => {
      state.debugTimeOfDay = action.payload
    },
    advanceFreePlayTimeOfDay: (state) => {
      state.freePlayTimeOfDay = getNextTimeOfDay(state.freePlayTimeOfDay)
      state.freePlayTimeSlotConsumed = false
      state.freePlayHasAdvancedOnce = true
    }

  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => endSceneTypes.has(action.type),
      (state) => {
        if (state.isNoDelay) {
          state.freePlayTimeSlotConsumed = true
        }
      }
    )
  }
})

export const { setTimescaleMode, setNoDelayMode, setNoNotification, setUseNotifications, setGameHasStarted, setDebugTimeOfDay, advanceFreePlayTimeOfDay } = settingsSlice.actions

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectIsNoDelay = ({ settings: state }) => {
  return state.isNoDelay
}
export const selectIsNoNotifications = ({ settings: state }) => {
  return state.isNoNotifications
}
export const selectHasGameStarted = ({ settings: state }) => {
  return state.hasStartedGame
}
export const selectGcdTimeSeconds = ({ settings: state }) => {
  return state.gcdTimeSeconds
}
export const selectDebugTimeOfDay = ({ settings: state }) => {
  return state.debugTimeOfDay
}
export const selectFreePlayTimeOfDay = ({ settings: state }) => {
  return state.freePlayTimeOfDay
}
export const selectFreePlayTimeSlotConsumed = ({ settings: state }) => {
  return state.freePlayTimeSlotConsumed
}
export const selectFreePlayHasAdvancedOnce = ({ settings: state }) => {
  return state.freePlayHasAdvancedOnce
}

export default settingsSlice.reducer
